/**
 * useAISummary Hook
 * Custom hook for managing AI summary generation state and logic
 */

import { useState } from 'react';
import { generateSummary as generateSummaryService, generateSummaryStream as generateSummaryStreamService } from '../services/api/aiService';

/**
 * Hook for managing AI-powered summary generation
 * @returns {Object} Object containing generate function and state variables
 * @returns {Function} generate - Function to call the API and generate a summary
 * @returns {boolean} isLoading - Whether the API call is in progress
 * @returns {string|null} error - Error message if the API call failed
 * @returns {string|null} result - The generated summary text
 */
export const useAISummary = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  /**
   * Generates a summary for the provided messages array
   * @param {Array} messages - Complete messages array with role and content objects
   * @param {Object} options - Optional generation overrides (maxTokens, temperature, etc.)
   */
  const generate = async (messages, options = {}) => {
    // Reset previous error
    setError(null);
    setIsLoading(true);

    try {
      const summary = await generateSummaryService(messages, options);
      setResult(summary);
      return summary;
    } catch (err) {
      const errorMessage = err.message || 'Failed to generate summary';
      setError(errorMessage);
      setResult(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Streams a generation, calling onChunk(deltaText, accumulatedText) on each piece.
   * @param {Array} messages - Complete messages array
   * @param {Object} options - Optional generation overrides
   * @param {Function} onChunk - Called with (deltaText, accumulatedText) per chunk
   * @returns {Promise<string>} Full accumulated response
   */
  const generateStream = async (messages, options = {}, onChunk) => {
    setError(null);
    setIsLoading(true);

    try {
      const fullText = await generateSummaryStreamService(messages, options, onChunk);
      setResult(fullText);
      return fullText;
    } catch (err) {
      const errorMessage = err.message || 'Failed to generate summary';
      setError(errorMessage);
      setResult(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resets the hook state (useful for clearing previous results)
   */
  const reset = () => {
    setIsLoading(false);
    setError(null);
    setResult(null);
  };

  return {
    generate,
    generateStream,
    reset,
    isLoading,
    error,
    result,
  };
};

export default useAISummary;
